import styled from 'styled-components';

import { Text } from '@/nova/components';
import { CFG } from '../../../../../../theme/configurator-tokens';
import { hiddenScrollbar, cardHover } from '../../../../../../theme/configurator-keyframes';
import { FINISH } from '../../finish.tokens';
import { SPEI, SPEI_QA } from '../../spei.tokens';

export const Root = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
`;

export const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 16px;
  ${hiddenScrollbar};
`;

/* The mark repeats here, smaller than on the form: by this screen the buyer already knows
   which rail they picked, so it is a reminder rather than the headline. */
export const LogoRow = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 12px;
`;

export const Title = styled(Text)`
  font-size: 16px;
  line-height: 1;
  color: ${CFG.foreground};
`;

export const Lead = styled(Text)`
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.45;
  color: ${CFG.textMuted};
`;

/* Amount is called out above the coordinates: it is the one number that must match
   exactly, and a mistyped amount is the most common reason a SPEI transfer is not
   auto-matched to an order. It carries its own copy button rather than being repeated
   as another row in the panel below. */
export const AmountBlock = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  /* No card: the amount reads as a headline figure, not another coordinate row. The 14px
     horizontal padding stays even without a background — it keeps the label and the copy
     button aligned to the same columns as the panel rows below. */
  padding: 4px 14px;
`;

export const AmountLabels = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

export const AmountLabel = styled(Text)`
  font-size: 13px;
  line-height: 1.2;
  color: ${SPEI.amountLabel};
`;

export const AmountValue = styled(Text)`
  overflow-wrap: anywhere;
  /* 24px + 25%. With the card gone this is what carries the emphasis instead. */
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  color: ${SPEI.amountValue};
`;

export const Panel = styled.div`
  margin-top: 12px;
  border-radius: 14px;
  border: 1px solid ${SPEI.panelBorder};
  background: ${SPEI.panelBg};
`;

export const Notice = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid ${SPEI.noticeBorder};
  background: ${SPEI.noticeBg};
  padding: 12px 14px;
`;

export const NoticeIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  align-items: center;
  justify-content: center;
`;

export const NoticeText = styled(Text)`
  font-size: 14px;
  line-height: 1.4;
  color: ${SPEI.noticeText};
`;

export const Expiry = styled(Text)`
  margin-top: 10px;
  font-size: 13px;
  line-height: 1.35;
  color: ${SPEI.expiryText};
`;

/* ------------------------------- how to pay -------------------------------- */

export const StepsTitle = styled(Text)`
  margin-top: 24px;
  font-size: 14px;
  line-height: 1;
  color: ${CFG.foreground};
`;

export const StepsList = styled.ol`
  margin: 12px 0 0;
  display: flex;
  list-style: none;
  flex-direction: column;
  gap: 10px;
  padding: 0;
`;

export const StepRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

export const StepIndex = styled.span`
  display: flex;
  height: 22px;
  width: 22px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: ${SPEI.stepIndexBg};
  font-size: 12px;
  font-weight: 700;
  color: ${SPEI.stepIndexText};
`;

export const StepText = styled(Text)`
  font-size: 14px;
  line-height: 1.4;
  color: ${SPEI.stepText};
`;

/* --------------------------------- footer ---------------------------------- */

export const Footer = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${FINISH.paneDivider};
  padding: 16px 12px;
`;

export const ContinueButton = styled.button`
  display: flex;
  height: 48px;
  width: 100%;
  cursor: pointer;
  ${cardHover}
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: none;
  background: ${CFG.brand};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${FINISH.payButtonText};
  transition: opacity 150ms ease;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

export const ContinueLabel = styled(Text)`
  display: inline;
`;

/* --------------------------- QA settlement (test-only) --------------------------- */

/* A floating debug control that opens the commerce-gate test settlement URL. Styled to be
   unmistakably a developer tool: a dashed magenta pill floating over the pane, in no theme's
   palette, so it can never read as the real pay / continue button. Anchored to `Root`
   (position: relative) and sitting above the footer's Continue button. Rendered ONLY when a
   non-production invoice carried a test URL, so a real buyer never sees it. */
export const QaFinalizeButton = styled.button`
  position: absolute;
  right: 12px;
  bottom: 84px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border-radius: 9999px;
  border: 1px dashed ${SPEI_QA.border};
  background: ${SPEI_QA.bg};
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  color: ${SPEI_QA.text};
`;

export const QaFinalizeLabel = styled(Text)`
  display: inline;
`;
